import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { articles } from "@/lib/blog-data";
import { PartnerPromoBlock } from "@/components/site/PartnerPromoBlock";

export const Route = createFileRoute("/blog/$slug")({
  loader: ({ params }) => {
    const article = articles.find((a) => a.slug === params.slug);
    if (!article) throw notFound();
    return { article };
  },
  head: ({ loaderData }) => {
    const a = loaderData?.article;
    if (!a) return { meta: [{ title: "Article — Rénergescence" }] };
    return {
      meta: [
        { title: `${a.title} — Rénergescence` },
        { name: "description", content: a.excerpt },
        { property: "og:title", content: a.title },
        { property: "og:description", content: a.excerpt },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `/blog/${a.slug}` },
        { property: "og:image", content: a.image },
      ],
      links: [{ rel: "canonical", href: `https://renergescence.com/blog/${a.slug}` }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: a.title,
            description: a.excerpt,
            image: a.image,
            datePublished: a.date,
            author: { "@type": "Person", name: "Caroline Balech" },
            publisher: {
              "@type": "Organization",
              name: "Rénergescence",
            },
            mainEntityOfPage: `https://renergescence.com/blog/${a.slug}`,
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="py-32 text-center">
      <h1 className="font-display text-4xl italic">Article introuvable</h1>
      <Link to="/blog" className="mt-6 inline-block text-sage underline" style={{ color: "var(--color-sage-text)" }}>Retour au blog</Link>
    </div>
  ),
  component: ArticlePage,
});

function ArticlePage() {
  const { article } = Route.useLoaderData();

  return (
    <article className="pt-12 pb-24">
      <div className="mx-auto max-w-[720px] px-6">
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm" style={{ color: "var(--color-sage-text)" }}>
          <ArrowLeft size={14} /> Retour au blog
        </Link>
      </div>

      <header className="mx-auto mt-8 max-w-[720px] px-6">
        <span className="eyebrow">{article.category}</span>
        <h1 className="mt-4 font-display text-4xl font-light italic leading-tight md:text-6xl">{article.title}</h1>
        <p className="mt-5 text-sm" style={{ color: "var(--color-muted-foreground)" }}>
          {article.date} · {article.readTime}
        </p>
      </header>

      <div className="mx-auto mt-10 max-w-[900px] px-6">
        <div className="overflow-hidden rounded-2xl">
          <img src={article.image} alt={article.imageAlt || article.title} width={1024} height={640} className="w-full object-cover" />
        </div>
      </div>

      <div className="mx-auto mt-12 max-w-[720px] px-6 text-lg leading-relaxed" style={{ color: "var(--color-text)" }}>
        {article.bodyHtml ? (
          <div
            className="article-prose space-y-5 [&_h3]:font-display [&_h3]:text-2xl [&_h3]:md:text-3xl [&_h3]:font-light [&_h3]:italic [&_h3]:mt-10 [&_h3]:mb-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2 [&_a]:underline [&_a]:text-[var(--color-sage)] [&_p]:leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.bodyHtml }}
          />
        ) : (
          <div className="space-y-5">
            {article.body.map((p: string, i: number) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        )}

        {article.partner && (
          <PartnerPromoBlock
            brandName={article.partner.brandName}
            code={article.partner.code}
            shopUrl={article.partner.shopUrl}
            description={article.partner.description}
          />
        )}
      </div>
    </article>
  );
}
